import { CommonModule } from '@angular/common'
import { Component, OnDestroy, inject, signal } from '@angular/core'
import { Router } from '@angular/router'
import { MessageService } from '@openng/optimus-ui/api'
import { Subscription } from 'rxjs'
import { OptimUiSharedModule } from '../../../shared/optimus-ui-shared.module'
import { SupportTicketFormComponent } from '../../../_shared/support/support-ticket-form.component'
import { FuseqRaidenService } from '../../fuseq-raiden/fuseqRaiden.service'
import {
  DEFAULT_SUPPORT_CATEGORIES,
  SupportCategory,
  SupportTicket,
  SupportTicketApiService,
  SupportTicketCategory,
  SupportTicketStatus
} from '../../../_services'

/**
 * Icona "Support" nella top bar: qualsiasi utente può inviare una
 * segnalazione al team di sviluppo e vedere lo stato delle proprie.
 * La gestione (tutte le segnalazioni) è nella pagina /support/manage.
 */
@Component({
  selector: 'fuseq-support-menu',
  standalone: true,
  imports: [OptimUiSharedModule, CommonModule, SupportTicketFormComponent],
  templateUrl: './support-menu.component.html'
})
export class SupportMenuComponent implements OnDestroy {
  private readonly api = inject(SupportTicketApiService)
  private readonly messageService = inject(MessageService)
  private readonly router = inject(Router)
  private readonly fuseqRaiden = inject(FuseqRaidenService)
  private readonly subscription = new Subscription()

  readonly count = signal({ mine: 0, all: 0, canManage: false })
  readonly tickets = signal<SupportTicket[]>([])
  readonly loading = signal(false)
  readonly view = signal<'new' | 'mine'>('new')

  showDialog = false

  readonly categories = signal<SupportCategory[]>(DEFAULT_SUPPORT_CATEGORIES)

  constructor () {
    this.refreshCount()
    this.loadCategories()
    // Push Raiden: aggiorna badge e (se aperto) la lista "Le mie segnalazioni".
    this.subscription.add(this.fuseqRaiden.onNewEvent().subscribe((event: any) => {
      if (typeof event?.type === 'string' && event.type.startsWith('SUPPORT_TICKET')) {
        this.refreshCount()
        if (this.showDialog && this.view() === 'mine') {
          this.loadMine()
        }
      }
    }))
  }

  ngOnDestroy (): void {
    this.subscription.unsubscribe()
  }

  badgeCount (): number {
    return this.count().canManage ? this.count().all : this.count().mine
  }

  toggleDialog (): void {
    this.showDialog = !this.showDialog
    if (this.showDialog) {
      this.refreshCount()
      this.loadMine()
    }
  }

  openView (view: 'new' | 'mine'): void {
    this.view.set(view)
    if (view === 'mine') {
      this.loadMine()
    }
  }

  openManage (): void {
    this.showDialog = false
    void this.router.navigate(['/support/manage'])
  }

  /** Il form condiviso ha inviato la segnalazione: badge e "Le mie" aggiornati. */
  onTicketCreated (): void {
    this.refreshCount()
    this.openView('mine')
  }

  refreshCount (): void {
    this.api.getCount().subscribe({
      next: (data) => this.count.set(data),
      error: () => { /* l'icona resta utilizzabile, il badge è opzionale */ }
    })
  }

  loadCategories (): void {
    this.api.getCategories().subscribe({
      next: (data) => {
        if (!Array.isArray(data) || data.length === 0) {
          return
        }
        this.categories.set(data)
      },
      error: () => { /* resta il fallback DEFAULT_SUPPORT_CATEGORIES */ }
    })
  }

  loadMine (): void {
    this.loading.set(true)
    this.api.getMine().subscribe({
      next: (data) => {
        this.tickets.set(Array.isArray(data) ? data : [])
        this.loading.set(false)
      },
      error: (error) => {
        this.loading.set(false)
        if (!error.isintercepted) {
          this.messageService.add({ severity: 'error', summary: 'Supporto', detail: error?.error?.message ?? 'Errore durante il caricamento' })
        }
      }
    })
  }

  statusLabel (status: SupportTicketStatus): string {
    if (status === 'in_progress') {
      return 'In progress'
    }
    if (status === 'done') {
      return 'Done'
    }
    return status === 'closed' ? 'Closed' : 'Open'
  }

  statusClass (status: SupportTicketStatus): string {
    if (status === 'in_progress') {
      return 'fq-badge-warning'
    }
    if (status === 'done') {
      return 'fq-badge-success'
    }
    return status === 'closed' ? 'fq-badge-secondary' : 'fq-badge-danger'
  }

  categoryLabel (category: SupportTicketCategory): string {
    return this.categories().find((c) => c.code === category)?.label ?? category
  }

  formatDate (value: string | null): string {
    if (!value) {
      return '-'
    }
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })
  }
}
